import React, { useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import "@mobile/index.css";
import { AppRoutes } from "@mobile/app/routes";
import { AppOverlays, AppProviders } from "@mobile/app/providers";
import { preRenderSetup } from "@mobile/init";
import { DebugPanel } from "@mobile/features/debug/debug-panel";
import { listenForSwUpdate } from "@mobile/lib/sw-update";
import { PatchPilotProvider } from "@repo/patch-pilot-sdk/react";
import { appStores, useAuthStore } from "@mobile/stores";
import { storeDeps } from "@mobile/stores/deps";
import { Config } from "@mobile/config/env";
import { SentryFallback } from "@repo/share-log-ui/ui/sentry-fallback";

/** FAB과 동일한 bottom 위치: 56px(탭바) + 16px(간격) + safe-area-inset-bottom */
function useFabOffset() {
  return useMemo(() => {
    const safeArea = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--safe-area-inset-bottom",
      ),
      10,
    );
    return { bottom: 56 + 16 + (safeArea || 0) };
  }, []);
}

function PatchPilotConnector() {
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const userInfo = useMemo(
    () => (user ? { email: user.email } : undefined),
    [user?.email],
  );
  const offset = useFabOffset();

  if (!pathname.startsWith("/more")) return null;

  return (
    <PatchPilotProvider
      apiKey={Config.PATCH_PILOT_API_KEY}
      apiUrl={Config.PATCH_PILOT_API_URL}
      user={userInfo}
      offset={offset}
      theme="auto"
      locale="ko"
    />
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

const el = document.getElementById("root");
if (el) {
  preRenderSetup();
  const root = createRoot(el);
  root.render(
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppProviders stores={appStores} deps={storeDeps}>
            <AppRoutes />
            <AppOverlays />
            <DebugPanel />
            <PatchPilotConnector />
          </AppProviders>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>,
  );
  listenForSwUpdate();
} else {
  throw new Error("Could not find root element");
}
